import React, { useState, useEffect, useRef, useCallback } from 'react';
import FilterItem from '../FilterItem';
import Counter from '../../../components/Counter';
import { useDispatch, useSelector } from 'react-redux';
import { updateWhoFilter } from '../mainFilterSlice';
import { scrollToResults, deepEqual, isMobile } from '../../../utilities/helpers';
import { t } from '../../../utilities/translator';
import AgeSelect from '../../../components/AgeSelect';
import { pushEvent } from '../../../utilities/analytics';
import {
  WHO_FILTER_UPDATE_EVENT,
  OPEN_LOGIN_BLOCK,
  CLOSE_LOGIN_BLOCK,
  MAYBELATER_LOGIN_BLOCK,
  BIG_GROUP_CLICK,
} from '../../../constants/analyticEvents';
import {
  filterClearClick,
  selectIsAnyFilterOpened,
  sendSearchStatistic,
  setAppState,
  setLastChangedFilter,
} from '../../../appSlice';
import useHandleMainFilterState from '../../../hooks/mainFilter/useHandleMainFilterState';
import { selectFilterData } from '../selectors';
import { updateSearch } from '../searchSlice';
import useSearchPerformed from '../../../hooks/useSearchPerformed';
import LoginBlock from '../../user/LoginBlock';
import EngineTutorial from '../../customize-button/components/messages/EngineTutorial';
import GroupBookingBtn from '../../groupBooking/GroupBookingBtn';
import abTesting from '../../../services/abTesting';
import { BIG_GROUP_LINK } from '../../../constants/constants';

const initialValues = {
  adults: 2,
  children: 0,
  babies: 0,
  rooms: 1,
  childAges: [],
};

function Guests() {
  const clearTimerRef = useRef(null);
  const saveTimerRef = useRef(null);
  const groupTimerRef = useState(null);
  const dispatch = useDispatch();
  const { guestFilterTooltip, shouldStoreCapacity } = useSelector((state) => state.app);
  const anyFilterOpened = useSelector(selectIsAnyFilterOpened);
  const {
    searchEngine: { who },
  } = useSelector(selectFilterData);
  const { initial, ...restData } = who;
  const [isClean, setIsClean] = useState(true);
  const [adults, setAdults] = useState(who.adults || initialValues.adults);
  const [children, setChildren] = useState(who.children || initialValues.children);
  const [babies, setBabies] = useState(
    who.childAges.reduce((b, current) => {
      if (current < 2) {
        return b + 1;
      }

      return b;
    }, 0) || initialValues.babies
  );
  const [rooms, setRooms] = useState(who.rooms || initialValues.rooms);
  const [showRoomWarning, setShowRoomWarning] = useState(false);
  const [groupBooking, setGroupBooking] = useState(false);

  const [setAppTimeout] = useSearchPerformed();

  if (!restData.babies) {
    restData.babies = 0;
  }

  const [childrenAge, setChildrenAge] = useState(who.childAges || initialValues.childAges);

  const previousValuesRef = useRef({
    adults,
    children,
    rooms,
    childAges: childrenAge,
  });

  const [value, setValue] = useState('');
  const showClearButton = !deepEqual(initialValues, restData);
  const [isOpen, setIsOpen] = useHandleMainFilterState({ filter: 'who' });

  const userContext = JSON.parse(localStorage.getItem('hf_user'));
  const loginWasClosed = +localStorage.getItem('loginClosed');
  const [isLoginOpen, setLoginOpen] = useState(false);
  const [showLoginBtn, setShowLoginBtn] = useState(!((loginWasClosed > Date.now()) || (userContext && userContext?.user?.userLevel === 'USER_EMAIL' && userContext?.user?.user_auth)));
  const loginTriggerTxt = "Passengers filter";

  const onTooltipClick = useCallback(() => {
    if (guestFilterTooltip) {
      dispatch(
        setAppState({
          guestFilterTooltip: false,
          shouldStoreCapacity: true,
        })
      );
      setIsOpen(true);
    }
  }, [dispatch, setIsOpen, guestFilterTooltip]);

  useEffect(() => {
    if (!((loginWasClosed > Date.now()) || (userContext && userContext?.user?.userLevel === 'USER_EMAIL' && userContext?.user?.user_auth)) && isOpen && !showLoginBtn) {
      setShowLoginBtn(true);
    }
  }, [loginWasClosed, userContext?.user?.userLevel, userContext?.user?.user_auth, isOpen]);

  useEffect(() => {
    clearTimeout(clearTimerRef.current);
    clearTimeout(saveTimerRef.current);
  }, []);

  useEffect(() => {
    const currentData = {
      adults,
      children,
      babies,
      rooms,
      childAges: childrenAge,
    };

    setIsClean(deepEqual(currentData, restData));
  }, [adults, children, babies, rooms, childrenAge, who]);

  useEffect(() => {
    const getLabelValue = () => {
      return `${who.adults} ${t('general.adults_plural', { count: who.adults })}
      , ${
        who.children - babies > 0
          ? who.children -
            babies +
            ` ${t('general.children_plural', { count: who.children - babies })}
      ,`
          : ''
      } ${
        babies > 0
          ? babies +
            ` ${t('general.infants_plural', { count: babies })}
      ,`
          : ''
      } ${who.rooms} ${t('general.rooms_plural', { count: who.rooms })}
      `;
    };

    setValue(getLabelValue());
  }, [who]);

  useEffect(() => {
    if (adults > 2 && children > 2) {
      setChildren(2);
    }
  }, [adults, children]);

  useEffect(() => {
    setChildrenAge(who.childAges);
  }, [who.childAges]);

  useEffect(() => {
    if (children > childrenAge.length) {
      setChildrenAge((childrenAge) => [...childrenAge, 7]);
    } else {
      setChildrenAge((childrenAge) => childrenAge.slice(0, children));
    }
  }, [children]);

  useEffect(() => {
    const newBabies = childrenAge.reduce((b, current) => { if (current < 2) { return (b + 1); } return b; }, 0);
    setBabies(newBabies);
  }, [children, childrenAge]);

  useEffect(() => {
    if (guestFilterTooltip && anyFilterOpened) {
      dispatch(setAppState({ guestFilterTooltip: false }));
    }
  }, [dispatch, anyFilterOpened, guestFilterTooltip]);

  const onOpenLogin = () => {
    setLoginOpen(true);
    // eslint-disable-next-line no-undef
    if (typeof closeSubscribeLogin !== 'undefined') {
      // eslint-disable-next-line no-undef
      closeSubscribeLogin();
    }
    pushEvent(OPEN_LOGIN_BLOCK, { sourceBlock: loginTriggerTxt });
  };

  const loginPopupClose = () => {
    setLoginOpen(false);
    setShowLoginBtn(false);
    pushEvent(CLOSE_LOGIN_BLOCK, { sourceBlock: loginTriggerTxt });
  };

  const loginPopupCloseLogIn = () => {
    setLoginOpen(false);
    setShowLoginBtn(false);
  };

  useEffect(() => {
    window.addEventListener('userLoggedIn', loginPopupCloseLogIn);

    return () => {
      window.removeEventListener('userLoggedIn', loginPopupCloseLogIn);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      loginPopupCloseLogIn();
    }
  }, [isOpen]);

  const handleSetIsOpen = useCallback(
    (value) => {
      if (guestFilterTooltip) {
        onTooltipClick();
      } else {
        setIsOpen(value);
      }
    },
    [guestFilterTooltip, onTooltipClick, setIsOpen]
  );

  const storeSelectedCapacity = (data) => {
    if (adults > 2 || children > 0 || babies > 0) {
      localStorage.setItem(
        'familyCapacityDetails',
        JSON.stringify({
          ...data,
          timestamp: Date.now(),
        })
      );
    }

    dispatch(setAppState({ shouldStoreCapacity: false }));
  };

  const handleSaveClick = (e, noLogin = false) => {
    const data = {
      adults,
      children,
      babies,
      rooms,
      childAges: childrenAge,
    };

    if (rooms > 1) {
      dispatch(
        sendSearchStatistic({
          action: 'capacity',
          ...data,
        })
      );
      return;
    }

    if (shouldStoreCapacity) {
      storeSelectedCapacity(data);
    }

    if (deepEqual(data, previousValuesRef.current)) return undefined;

    // temporary hide login
    /* if (showLoginBtn && !noLogin && !isMobile()) {
      onOpenLogin();
      return false;
    } */

    previousValuesRef.current = data;
    saveTimerRef.current = setTimeout(() => {
      dispatch(updateWhoFilter(data));
      dispatch(updateSearch({ who: data }));
      dispatch(setLastChangedFilter('who'));
    }, 0);
    pushEvent(WHO_FILTER_UPDATE_EVENT, data);
    setAppTimeout(7000);
    setIsOpen(false);
    scrollToResults();
    setGroupBooking(false);
    clearTimeout(groupTimerRef.current);
  };

  const loginPopupCloseLater = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setLoginOpen(false);
    setShowLoginBtn(false);
    localStorage.setItem('loginClosed', +tomorrow);
    setTimeout(() => handleSaveClick(false, true), 110);
    pushEvent(MAYBELATER_LOGIN_BLOCK, { sourceBlock: loginTriggerTxt });
  };

  const handleClearClick = () => {
    const data = {
      adults,
      children,
      babies,
      rooms,
      childAges: childrenAge,
    };

    setAdults(2);
    setChildren(0);
    setBabies(0);
    setChildrenAge([]);
    setRooms(1);
    setGroupBooking(false);
    clearTimeout(groupTimerRef.current);

    if (shouldStoreCapacity) {
      dispatch(setAppState({ shouldStoreCapacity: false }));
    }

    if (deepEqual(data, initialValues)) return undefined;
    dispatch(filterClearClick());
    clearTimerRef.current = setTimeout(() => {
      dispatch(updateWhoFilter(initialValues));
    }, 0);
    previousValuesRef.current = initialValues;

    scrollToResults();
  };

  const updateGroupBookingState = (adults, children) => {
    if (adults > 3) {
      setGroupBooking(true);
      return;
    }

    setGroupBooking(adults + children > 5)
  }

  const changeChildAge = (ind) => (value) => {
    const newAge = childrenAge.map((chAge, i) => (ind === i ?  value : chAge));
    const newBabies = newAge.reduce((b, current) => { if (current < 2) { return (b + 1); } return b; }, 0);
    setBabies(newBabies);
    setChildrenAge(newAge);
  };

  const onClickOutsideReset = () => {
    setAdults(who.adults || initialValues.adults);
    setChildren(who.children || initialValues.children);
    setRooms(who.rooms || initialValues.rooms);
    setShowRoomWarning(false);
    setGroupBooking(false);
    clearTimeout(groupTimerRef.current);
  };

  const handleSetAdults = (value) => {
    if (value > 3) {
      setAdults(3);
    } else {
      setAdults(value);
    }

    updateGroupBookingState(value, children);
    setShowRoomWarning(false);
  };

  const handleSetChildren = (value) => {
    if (value > 3) {
      setChildren(adults > 2 ? 2 : 3);
    } else {
      setChildren(value);
    }

    updateGroupBookingState(adults, value);
    setShowRoomWarning(false);
  };

  const handleSetRooms = (value) => {
    if (value > 1) {
      setRooms(1);
      setShowRoomWarning(true);
      groupTimerRef.current = setTimeout(() => setGroupBooking(true), 2000);
      return;
    }
    setGroupBooking(false);
    setRooms(value);
  };

  return (
    <FilterItem
      label={t('mainFilter.whos_going')}
      eventName="who"
      value={value}
      onSave={handleSaveClick}
      onClear={handleClearClick}
      className="pull-right"
      icon="ri-user-line"
      showClearButton={showClearButton}
      clean={isClean}
      resetOnClickOutside={onClickOutsideReset}
      isOpen={isOpen}
      setIsOpen={handleSetIsOpen}
      showTooltip={guestFilterTooltip}
      tooltipMessage={t('educational.guestFilterTooltip')}
      onTooltipClick={onTooltipClick}
      isLoginOpen={isLoginOpen}
    >
      <>
        <div className="main-filter__item-wrap">
          <div className="guests">
            <div className="guests-counter">
              <div className="guests-counter__text">
                <span> {t('mainFilter.Adults')}</span>
                <span>{t('mainFilter.Ages_17_or_above')}</span>
              </div>
              <Counter value={adults} min={1} max={4} onChange={handleSetAdults} />
            </div>

            <div className="guests-counter">
              <div className="guests-counter__text">
                <span> {t('mainFilter.Children')}</span>
                <span>{t('mainFilter.Ages_0_17')}</span>
              </div>
              <Counter
                value={children}
                min={0}
                max={adults > 2 ? 3 : 4} // TODO: from server {initial.maxChilds}
                onChange={handleSetChildren}
              />
              {childrenAge.length !== 0 && (
                <div className="custom-select__wrap">
                  {childrenAge.map((value, ind) => (
                    <AgeSelect
                      key={ind}
                      value={value}
                      label={`${t('mainFilter.Child')} ${ind + 1}  ${t('mainFilter.age')}`}
                      changeChildAge={changeChildAge(ind)}
                      infantsCount={childrenAge.filter((age) => age < 2).length}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="guests-counter">
              <div className="guests-counter__text">
                <span> {t('mainFilter.rooms')}</span>
              </div>
              <Counter value={rooms} min={1} max={2} onChange={handleSetRooms} type="rooms" />
            </div>

            {showRoomWarning && !abTesting.groupBooking && (
              <EngineTutorial delay={0}>
                {t('mainFilter.whoGoingComingSoon')}
              </EngineTutorial>
            )}
            {groupBooking && !abTesting.groupBooking && (
              <div className="mt-4 p-3 rounded bg-info-light text-start scale-in-center">
                <p className="label-large leading-none mb-3">{t('mainFilter.who.bigGroupTitle')}</p>
                <p className="body-medium font-medium mb-0" onClick={() => { pushEvent(BIG_GROUP_CLICK, { type: 'engine' }); }} dangerouslySetInnerHTML={{ __html: t('mainFilter.who.bigGroupText', { link: BIG_GROUP_LINK }) }} />
              </div>
            )}
            {(groupBooking || showRoomWarning) && abTesting.groupBooking && (
              <GroupBookingBtn
                type={showRoomWarning ? 'room' : 'capacity'}
              />
            )}
          </div>
        </div>
        {!(userContext && userContext?.user?.userLevel === 'USER_EMAIL' && userContext?.user?.user_auth) &&
          <LoginBlock
            className="login-block-who"
            onClose={loginPopupClose}
            onCloseLater={loginPopupCloseLater}
            isOpen={isLoginOpen}
            type="who"
            sourceBlock={loginTriggerTxt}
          />
        }
      </>
    </FilterItem>
  );
}

export default React.memo(Guests);
